import React, { PropsWithChildren, useMemo } from 'react';
import classNames from 'classnames';
import { NativeProps } from '@/src/components/interfaces';

export enum CTextColor {
  Black = 'black',
  Error = 'error',
  Success = 'success',
  Warning = 'warning',
  Info = 'info',
  Link = 'link',
  Disabled = 'disabled',
  White = 'white',
  Gray1 = 'gray-1',
  Gray2 = 'gray-2',
  Gray3 = 'gray-3',
  Gray4 = 'gray-4',
  Gray5 = 'gray-5',
  Gray6 = 'gray-6',
  Gray7 = 'gray-7',
  Gray8 = 'gray-8',
  Gray9 = 'gray-9',
}

export enum CTextAlign {
  Left = 'left',
  Center = 'center',
  Right = 'right',
}

export enum CTextWeight {
  Thin = 'thin',
  Light = 'light',
  Regular = 'regular',
  Medium = 'medium',
  SemiBold = 'semi-bold',
  Bold = 'bold',
}

export enum CTextSize {
  Xxxlg = 'xxxlg',
  Xxlg = 'xxlg',
  Xlg = 'xlg',
  Lg = 'lg',
  Md = 'md',
  Sm = 'sm',
  Xs = 'xs',
}

export enum CTextDecoration {
  Underline = 'underline',
}

export enum CTextTransform {
  Uppercase = 'uppercase',
  Lowercase = 'lowercase',
}

export type CTextElement = 'span' | 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

export interface CTextProps extends NativeProps {
  weight?: CTextWeight;
  size?: CTextSize;
  as?: CTextElement;
  align?: CTextAlign;
  color?: CTextColor;
  decoration?: CTextDecoration;
  transform?: CTextTransform;
  animated?: boolean;
}

export function CText(props: PropsWithChildren<CTextProps>): React.JSX.Element {
  const weight = props.weight ?? CTextWeight.Regular;
  const size = props.size ?? CTextSize.Lg;
  const align = props.align ?? CTextAlign.Left;

  const Tag = props.as ?? 'span';

  const args = useMemo(() => {
    return {
      id: props.id,
      ['data-test-selector']: props.testSelector,
      onClick: props.onClick,
      className: classNames('c-text', `c-text--weight-${weight}`, `c-text--size-${size}`, `c-text--align-${align}`, {
        'c-text--animated': props.animated,
        [`c-text--color-${props.color}`]: props.color,
        [`c-text--decoration-${props.decoration}`]: props.decoration,
        [`c-text--transform-${props.transform}`]: props.transform,
      }, props.class, 'no-select'),
    };
  }, [weight, size, align, props.onClick, props.color, props.decoration, props.transform]);

  return (
    <Tag {...args}>
      {props.children}
    </Tag>
  );
}